import { queryClient } from '@/lib/api-client.ts'
import { currentUserQuery } from '@/lib/actions.ts'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { CurrStageData, currStageQuery } from '@/lib/actions/getCurrStage.ts'
import { currStageIssueTypesQuery } from '@/lib/actions/getStageIssuesTypes.ts'
import {
  GetProjectsParticipationsReturnT,
  userProjectParticipationsQuery,
} from '@/lib/actions/getUserProjectParticipations.ts'
import { currStageUsersQuery } from '@/lib/actions/getCurrStageUsers.ts'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { LoaderFunctionArgs } from 'react-router-dom'
import { validateLastActiveStageId } from '@/routes/login/validateLastActiveStageId.tsx'
import { shouldClearActiveProjectStage } from '@/routes/root/lib/hooks/stageAccessError.ts'
import { clearActiveProjectStageOnce } from '@/routes/root/lib/hooks/clearActiveProjectStage.ts'
import { offlineSafeEnsureQueryData } from '@/lib/helpers/offlineSafeQuery.ts'
import { InfiniteData } from '@tanstack/react-query'

// Dev-only debug logging
const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[rootLoader]', ...args)

export type RootLoaderT = {
  stageId: string | number | undefined | null
  userId: number | undefined
  stageUsers: ProjectStagesUsersT | null | undefined
  stageIssueTypes: IssueTypeT[] | null | undefined
  stage: CurrStageData | null | undefined
} | null

export const rootLoader = async ({
  params,
  request,
}: LoaderFunctionArgs<{ projectStageId?: string }>): Promise<RootLoaderT | Response> => {
  try {
    const participationsQueryOptions = userProjectParticipationsQuery()
    const participationsQueryKey = participationsQueryOptions.queryKey

    // Handle participations with offline support
    let participationsData: InfiniteData<GetProjectsParticipationsReturnT | undefined> | undefined

    if (!navigator.onLine) {
      // Offline: use cached data only
      participationsData = queryClient.getQueryData(participationsQueryKey)
      if (!participationsData) {
        debug('Offline with no cached participations')
        return null
      }
    } else {
      // Online: try to fetch, fallback to cache
      try {
        participationsData = await queryClient.fetchInfiniteQuery(participationsQueryOptions)
      } catch {
        participationsData = queryClient.getQueryData(participationsQueryKey)
        if (!participationsData) throw new Error('No participations data')
      }
    }

    const userProjectParticipations = participationsData?.pages[0]?.projectParticipations

    // Handle user with offline support
    const userQuery = currentUserQuery()
    const userMe = await offlineSafeEnsureQueryData(
      queryClient,
      userQuery.queryKey,
      userQuery.queryFn,
    )

    // If offline with no cached user, return null
    if (!userMe) {
      debug('No user data available (offline with no cache)')
      return null
    }

    const userId = userMe.id

    debug('Executed, user active project stage:', userMe.activeProjectStage)

    let stageId = validateLastActiveStageId(userProjectParticipations, userMe.activeProjectStage)

    if (params?.projectStageId) stageId = params.projectStageId

    if (userProjectParticipations?.length === 0 || !stageId)
      return {
        stageId,
        userId: userId,
        stageUsers: null,
        stageIssueTypes: null,
        stage: null,
      }

    // Fetch stage data with offline support
    const stageUsersQuery = currStageUsersQuery(stageId)
    const stageIssueTypesQueryOptions = currStageIssueTypesQuery(stageId)
    const stageQueryOptions = currStageQuery(stageId, userId)
    // allSettled, not all: `Promise.all` rejects with whichever request failed
    // FIRST, so a transient 504 on the users request would hide a decisive 404 on
    // the stage itself and the repair below would never run for it.
    const [stageUsersResult, stageIssueTypesResult, stageResult] = await Promise.allSettled([
      offlineSafeEnsureQueryData(queryClient, stageUsersQuery.queryKey, stageUsersQuery.queryFn),
      offlineSafeEnsureQueryData(
        queryClient,
        stageIssueTypesQueryOptions.queryKey,
        stageIssueTypesQueryOptions.queryFn,
      ),
      offlineSafeEnsureQueryData(
        queryClient,
        stageQueryOptions.queryKey,
        stageQueryOptions.queryFn,
      ),
    ])

    if (
      stageUsersResult.status === 'rejected' ||
      stageIssueTypesResult.status === 'rejected' ||
      stageResult.status === 'rejected'
    ) {
      const stageErrors = [stageUsersResult, stageIssueTypesResult, stageResult]
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map(result => result.reason)
      // Clear activeProjectStage only when the stage the user has SELECTED is
      // really gone or no longer visible to them. Two earlier versions of this
      // block were too eager: the first PATCHed on every failed load even for
      // users who never had an active stage (a no-op write that still
      // invalidated ['user'] and reset in-progress form state on the settings
      // pages), and the second still PATCHed on ANY failure — so a transient
      // 504 or a flaky mobile connection wiped a perfectly valid selection and
      // the user came back to no stage at all. `shouldClearActiveProjectStage`
      // also requires that the stage that FAILED is the persisted one: a route
      // param overrides it above, so a stale deep link must not clear it.
      if (
        shouldClearActiveProjectStage({
          persistedActiveStage: userMe.activeProjectStage,
          failedStageId: stageId,
          errors: stageErrors,
        })
      ) {
        // These queries are still not aborted, so an old navigation can finish after a
        // newer one has persisted another stage. Its captured stageId and activeProjectStage
        // still agree with each other, which means the stage-id correlation cannot detect that
        // the navigation is stale. This guard only closes that late-write window; it does not
        // cancel the in-flight queries.
        if (request?.signal?.aborted !== true) await clearActiveProjectStageOnce(userId)
      }
      return {
        stageId: null,
        userId: userId,
        stageUsers: null,
        stageIssueTypes: null,
        stage: null,
      }
    }

    return {
      stageId,
      userId: userId,
      stageUsers: stageUsersResult.value,
      stageIssueTypes: stageIssueTypesResult.value,
      stage: stageResult.value,
    }
  } catch (err) {
    // Check if this is a network-related error (offline or connectivity issue)
    const isNetworkError =
      !navigator.onLine ||
      (err instanceof Error &&
        (err.message.toLowerCase().includes('network') ||
          err.message.toLowerCase().includes('failed to fetch') ||
          (err as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      debug('Network error, returning null to allow offline mode')
      return null
    }

    debug('Error in root:', err)
    return handleApiErr(err)
  }
}
